import { PhoneNumberUtil } from "google-libphonenumber";
import { ZodDiscriminatedUnion, z } from "zod";

import {
  AudienceContactField,
  buildZodSchemaForAcfColumnSelect,
  buildZodSchemaForAudienceContactFields,
} from "@stw/audience-contact-fields";

const phoneUtil = PhoneNumberUtil.getInstance();

function phoneNumberIsValidForUS(phoneNumber: string): boolean {
  try {
    const parsedNumber = phoneUtil.parse(phoneNumber, "US");
    const regionCode = phoneUtil.getRegionCodeForNumber(parsedNumber);

    return regionCode === "US" && phoneUtil.isPossibleNumber(parsedNumber);
  } catch (_) {
    return false;
  }
}

function BaseSubscriberFieldsSchema(
  audienceContactFields: AudienceContactField[]
) {
  return z.object({
    fields: buildZodSchemaForAudienceContactFields(
      audienceContactFields,
      "name"
    ),
  });
}

type BaseSubscriberFieldsSchemaType = {
  fields: z.ZodObject<Record<string, z.ZodTypeAny>>;
};

export function TextEntrySchema(
  audienceContactFields: AudienceContactField[]
): TextEntrySchemaType {
  return BaseSubscriberFieldsSchema(audienceContactFields).extend({
    uploadMethod: z.literal("textEntry"),
    phoneNumber: z
      .string()
      .refine(phoneNumberIsValidForUS, "Must be a valid US phone number"),
    sendWelcomeMessage: z.boolean().default(false),
  });
}

export type TextEntrySchemaType = z.ZodObject<
  {
    uploadMethod: z.ZodLiteral<"textEntry">;
    sendWelcomeMessage: z.ZodDefault<z.ZodBoolean>;
    phoneNumber: z.ZodEffects<z.ZodString>;
  } & BaseSubscriberFieldsSchemaType
>;

type UploadSchemaType = z.ZodObject<{
  uploadMethod: z.ZodLiteral<"fileUpload">;
  phoneNumberColumn: z.ZodString;
  firstNameColumn: z.ZodOptional<z.ZodString>;
  lastNameColumn: z.ZodOptional<z.ZodString>;
  sendWelcomeMessage: z.ZodBoolean;
  audienceFieldColumns: z.ZodObject<Record<string, z.ZodTypeAny>>;
}>;

export function UploadSchema(
  audienceContactFields: AudienceContactField[]
): UploadSchemaType {
  return z.object({
    uploadMethod: z.literal("fileUpload"),
    phoneNumberColumn: z.string(),
    firstNameColumn: z.string().optional(),
    lastNameColumn: z.string().optional(),
    sendWelcomeMessage: z.boolean(),
    audienceFieldColumns: buildZodSchemaForAcfColumnSelect(
      audienceContactFields,
      "name"
    ),
  });
}

export const AddSubscriberSchema = (
  audienceContactFields: AudienceContactField[]
): ZodDiscriminatedUnion<string, (UploadSchemaType | TextEntrySchemaType)[]> =>
  z.discriminatedUnion("uploadMethod", [
    TextEntrySchema(audienceContactFields),
    UploadSchema(audienceContactFields),
  ]);

export function UpdateSubscriberSchema(
  audienceContactFields: AudienceContactField[]
): z.ZodObject<
  {
    subscriberId: z.ZodString;
  } & BaseSubscriberFieldsSchemaType
> {
  return BaseSubscriberFieldsSchema(audienceContactFields).extend({
    subscriberId: z.string(),
  });
}

export type UpdateSubscriberSchemaType = z.input<
  ReturnType<typeof UpdateSubscriberSchema>
>;
